<template>
  <div id="order_food_pickup">
    <div class="main">
      <PointTransaction
        :title="$t('OrderFoodPickup.Title')"
        @getTypeData="handlePickupTypeData"
      />
    </div>

    <el-dialog
      class="KNS-dialog pickup-confirm-dialog"
      :visible.sync="pickupDialogVisible"
      width="900px"
      center
    >
      <span slot="title" class="dialog-title">
        <div class="title-bar">
          <span class="title">
            {{ $t('OrderFoodPickup.PickupConfirmTitle') }}
          </span>
        </div>
      </span>
      <div class="pickup-groups">
        <div
          v-for="(group, groupIndex) in pickupGroups"
          :key="group.tabletId"
          class="pickup-group"
        >
          <div class="group-label">
            {{ $t('OrderFoodPickup.HouseholdLabel', { note: group.tabletNote }) }}
          </div>
          <el-table
            :ref="'pickupTable_' + groupIndex"
            :data="group.forms"
            border
            class="pickup-table"
            @selection-change="(val) => handleSelectionChange(groupIndex, val)"
          >
            <el-table-column
              type="selection"
              width="60"
            />
            <el-table-column
              :label="$t('OrderFoodPickup.TableLabelOrderNumber')"
              width="130"
            >
              <template slot-scope="scope">
                {{ formatOrderCode(scope.row) }}
              </template>
            </el-table-column>
            <el-table-column
              :label="$t('OrderFoodPickup.TableLabelDiningInfo')"
              width="220"
            >
              <template slot-scope="scope">
                {{ formatDiningInfo(scope.row.dining) }}
              </template>
            </el-table-column>
            <el-table-column :label="$t('OrderFoodPickup.TableLabelFoodItems')">
              <template slot-scope="scope">
                <div
                  v-for="(item, itemIndex) in scope.row.items"
                  :key="itemIndex"
                >
                  {{ formatItem(item) }}
                </div>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>
      <div slot="footer">
        <el-button round @click="clearAllSelections">
          {{ $t('OrderFoodPickup.ClearSelection') }}
        </el-button>
        <el-button
          type="primary"
          round
          @click="handleCompletePickup"
        >
          {{ $t('OrderFoodPickup.CompletePickup') }}
        </el-button>
      </div>
    </el-dialog>

    <RepeatedOpeningDialog ref="repeatedDialog" />
  </div>
</template>

<script>
import PointTransaction from './components/PointTransaction'
import RepeatedOpeningDialog from '@/components/RepeatedOpeningDialog'
import { apiOrderFood } from '@/api/OrderFood'
import { convertKeysToCamelCase, convertKeysToPascalCase } from '@/js/utils/bff'
import { formatOrderDisplayCode, DiningOption } from '@/js/utils/orderFood'

export default {
  name: 'OrderFoodPickup',
  components: {
    PointTransaction,
    RepeatedOpeningDialog
  },
  data () {
    return {
      pickupDialogVisible: false,
      pickupGroups: [], // [{ tabletId, tabletNote, forms: [...] }]
      pickupSelections: {}, // { groupIndex: [selectedRows] }
      pickupType: 1, // 領取類型(1:直接選擇戶別、2:手機條碼、3:門禁卡、磁扣)
      pickupCode: '' // 領取識別碼(戶別: 空值, 手機條碼/載具: 掃描或感應裝置取得的原始代碼)
    }
  },
  methods: {
    /**
     * 接收住戶取餐戶別資料
     * @param {number} _type 扣點方式(1: 直接選擇戶別, 2: 手機條碼, 3: 門禁卡、磁扣)
     * @param {array} tablets 戶別資料陣列
     * @param {string} code 掃描/感應裝置取得的原始代碼（type 2/3 適用）
     */
    handlePickupTypeData (_type, tablets, code = '') {
      // 記錄領取類型與識別碼（供 handleCompletePickup 使用）
      this.pickupType = _type
      this.pickupCode = code

      // 統一欄位名稱（PointTransaction 透過 convertKeysToCamelCase 回傳 camelCase，
      // HouseholdFrame 直接回傳 PascalCase）
      const normalized = tablets.map(t => ({
        TabletId: t.TabletId || t.tabletId,
        TabletNote: t.TabletNote || t.tabletNote
      }))

      if (normalized.length >= 2) {
        // 多筆戶別：讓使用者透過 RepeatedOpeningDialog 選擇
        this.$refs.repeatedDialog.alert(normalized, (selected) => {
          if (selected && selected.length > 0) {
            this.fetchPickupPending(selected)
          }
        }, 'receive')
      } else {
        this.fetchPickupPending(normalized)
      }
    },
    /**
     * 取得待取餐列表並開啟取餐確認對話框
     * @param {array} tablets 選定戶別陣列 [{ TabletId, TabletNote }]
     */
    async fetchPickupPending (tablets) {
      const loading = this.$loading({
        lock: true,
        text: this.$t('General.PleaseWaitFor', { action: this.$t('General.Loading') }),
        spinner: 'el-icon-loading',
        background: 'rgba(0, 0, 0, 0.7)'
      })
      try {
        const tabletIds = tablets.map(t => t.TabletId)
        const params = convertKeysToPascalCase({
          tabletIds,
          startDateTime: dayjs().subtract(7, 'day').startOf('day').format(),
          endDateTime: dayjs().add(3, 'day').endOf('day').format(),
          offset: 0,
          maxCount: 10
        })
        const { status, data } = convertKeysToCamelCase(
          (await apiOrderFood.getTableFoodFormPickupPending(params)).data
        )
        if (status !== 1) return

        // 依戶別分組，保留原始順序
        const groupMap = {}
        const groupOrder = []
        ;(data.forms || []).forEach(form => {
          const key = form.tabletId
          if (!groupMap[key]) {
            groupMap[key] = { tabletId: form.tabletId, tabletNote: form.tabletNote, forms: [] }
            groupOrder.push(key)
          }
          groupMap[key].forms.push(form)
        })

        if (groupOrder.length === 0) {
          this.$message.warning(this.$t('OrderFoodPickup.NoPickupPending'))
          return
        }

        this.pickupGroups = groupOrder.map(key => groupMap[key])
        this.pickupSelections = {}
        this.pickupDialogVisible = true
        this.$nextTick(() => {
          this.pickupGroups.forEach((_, index) => {
            const tableRef = this.$refs[`pickupTable_${index}`]
            const table = Array.isArray(tableRef) ? tableRef[0] : tableRef
            if (table) {
              table.toggleAllSelection()
            }
          })
        })
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error)
      } finally {
        loading.close()
      }
    },
    /**
     * 記錄各戶別表格的勾選狀態
     * @param {number} groupIndex 戶別群組索引
     * @param {array} val 目前已勾選的訂單列表
     */
    handleSelectionChange (groupIndex, val) {
      this.$set(this.pickupSelections, groupIndex, val)
    },
    /**
     * 清除所有戶別表格的勾選狀態
     */
    clearAllSelections () {
      this.pickupGroups.forEach((_, index) => {
        const tableRef = this.$refs[`pickupTable_${index}`]
        const table = Array.isArray(tableRef) ? tableRef[0] : tableRef
        if (table) {
          table.clearSelection()
        }
      })
    },
    /**
     * 領取結單按鈕處理：確認後呼叫 API 完成取餐
     */
    async handleCompletePickup () {
      const selectedIds = Object.values(this.pickupSelections)
        .flat()
        .map(form => form.id)

      if (selectedIds.length === 0) {
        this.$message.warning(this.$t('OrderFoodPickup.PleaseSelectOrder'))
        return
      }

      try {
        await this.$confirm(
          this.$t('OrderFoodPickup.CompletePickupConfirm'),
          this.$t('General.AlertTitle'),
          {
            confirmButtonText: this.$t('General.Confirm'),
            cancelButtonText: this.$t('General.Cancel'),
            type: 'warning'
          }
        )
      } catch {
        return
      }

      const loading = this.$loading({ lock: true, text: this.$t('General.PleaseWaitFor', { action: this.$t('General.Processing') }), spinner: 'el-icon-loading', background: 'rgba(0, 0, 0, 0.7)' })
      try {
        const payload = convertKeysToPascalCase({
          ids: selectedIds,
          pickupType: this.pickupType,
          pickupCode: this.pickupCode,
          note: ''
        })
        const { status } = convertKeysToCamelCase(
          (await apiOrderFood.completeTableFoodFormPickup(payload)).data
        )
        if (status === 1) {
          this.$message.success(this.$t('OrderFoodPickup.CompletePickupSuccess'))
          this.pickupDialogVisible = false
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error)
      } finally {
        loading.close()
      }
    },
    /**
     * 格式化訂單顯示代碼（月日 + 訂單尾四碼）
     * @param {Object} row 訂單資料
     * @returns {string} 例如 "04161234"
     */
    formatOrderCode (row) {
      return formatOrderDisplayCode(row.comOrderId, row.createdAtUtc)
    },
    /**
     * 格式化用餐方式文字
     * - 內用 (diningOption=1)：「桌號：X」
     * - 預約 (diningOption=3)：「預約：YYYY/MM/DD HH:mm」
     * - 現場外帶 (diningOption=2)：「現場外帶」
     * @param {Object} dining 用餐方式資料
     * @returns {string}
     */
    formatDiningInfo (dining) {
      if (!dining) return '-'
      if (dining.diningOption === DiningOption.DineIn) {
        return this.$t('OrderFoodPickup.DiningInfoDineIn', { tableNo: dining.tableNo || '-' })
      }
      if (dining.diningOption === DiningOption.Reservation) {
        const time = dining.reservationAtUtc
          ? dayjs(dining.reservationAtUtc).format('YYYY/MM/DD HH:mm')
          : '-'
        return this.$t('OrderFoodPickup.DiningInfoReservation', { time })
      }
      return this.$t('OrderFoodPickup.DiningInfoTakeout')
    },
    /**
     * 格式化單一餐點品項文字
     * 格式：【類別名稱】品項名稱（規格）：數量
     * @param {Object} item 餐點品項資料
     * @returns {string}
     */
    formatItem (item) {
      const groupLabel = item.groupName ? `【${item.groupName}】` : ''
      const choosePart = item.chooseName ? `（${item.chooseName}）` : ''
      return `${groupLabel}${item.itemName}${choosePart}：${item.quantity}`
    }
  }
}
</script>

<style lang="scss" scoped>
#order_food_pickup {
  @include mainLayer();
  display: flex;
  justify-content: center;
  align-items: center;
  height: 70vh;

  .main {
    min-width: 960px;
    background-color: #fff;
    border-radius: 8px;
  }
}

.KNS-dialog {
  ::v-deep .el-dialog {
    @include KNSDialog();
  }
}

.pickup-groups {
  padding: 10px 20px 0;

  ::v-deep .el-table {
    @include basicTable;
  }
}

.pickup-group {
  margin-bottom: 32px;

  .group-label {
    font-size: map-get($fontSize, medium);
    font-weight: 600;
    color: #2f2f2f;
    margin-bottom: 8px;
  }
}

.pickup-confirm-dialog {
  & .el-button + .el-button {
    margin-left: 20px;
  }
}
</style>
